import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, FileText, Info, Search } from "react-feather";
import { toast } from "react-toastify";
import {
  deleteAgreementTemplate,
  getAgreementTemplateById,
  getAgreementTemplateErrorMessage,
  getAgreementTemplateList,
  searchAgreementTemplate,
} from "../../api/agreementTemplateApi";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Tab from "../../components/Tab";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import Input from "../../components/input/Input";
import SideDrawer from "../../components/input/SideDrawer";
import AgreementTemplateForm from "../../components/master/agreement-template/AgreementTemplateForm";
import AgreementTemplateRenderPreview from "../../components/master/agreement-template/AgreementTemplateRenderPreview";
import {
  ActiveCellStyle,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import {getBulkDropdownData} from "../../api/keyvalue";

const AgreementTemplate = () => {
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.AGREEMENT_TEMPLATE,
  });

  const [tab, setTab] = useState(1);

  // accordion & visibility flags (matches BlackList / DeductionItem pattern)
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);

  // data
  const [templates, setTemplates] = useState([]);
  const [searchRes, setSearchRes] = useState([]);


  // loading flags
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);

  // drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);


  const codeRef = useRef(null);
  const titleRef = useRef(null);
  const typeRef = useRef(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [language, setLanguage] = useState(null);
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState(null);

  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    (async () => {


      const {
        active,
        status
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
      ]);
      setActiveOptions(active);
      setStatusOptions(status);
    })();
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await getAgreementTemplateList();
      setTemplates(data || []);
      return data || [];
    } catch (error) {
      toast.error(
        getAgreementTemplateErrorMessage(error, "Failed to load templates."),
        { autoClose: 5000, containerId: "Main" }
      );
      return [];
    }
  };

  useEffect(() => {
    (async () => {
      setIsSearchLoading(true);
      await fetchTemplates();
      setIsSearchLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (tab === 2) {
      fetchTemplates();
    }
  }, [tab]);

  const languageOptions = useMemo(() => {
    const unique = Array.from(
      new Set(templates.map((item) => item.agtLanguage).filter(Boolean))
    );
    return unique.map((value) => ({ key: value, value, text: value }));
  }, [templates]);



  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    setActiveAccordion1(false);
    setResetVisible(true);
    setSearchClicked(true);
    setSearchSubmitLoading(true);

    const response = await searchAgreementTemplate({
      agtCode: codeRef.current?.value ?? "",
      agtTitle: titleRef.current?.value ?? "",
      agtType: typeRef.current?.value ?? "",
      agtLanguage: language,
      agtActive: active,
      agtStatus: status,
      fromDate: fromDate || null,
      toDate: toDate || null,
    });
    setSearchRes(response);
    setSearchSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    if (codeRef.current) codeRef.current.reset();
    if (titleRef.current) titleRef.current.reset();
    if (typeRef.current) typeRef.current.reset();
    setLanguage(null);
    setActive(null);
    setStatus(null);
    setFromDate("");
    setToDate("");
    setSearchRes([]);
    setSearchClicked(false);
    setResetVisible(false);
  };

  const openCreateDrawer = () => {
    setSelectedTemplate(null);
    setEditMode(false);
    setIsDrawerOpen(true);
  };

  const openEditDrawer = async (agtId) => {
    if (!agtId) return;
    setSearchFetchLoading(true);
    try {
      const response = await getAgreementTemplateById(agtId);
      setSelectedTemplate(response);
      setEditMode(true);
      setIsDrawerOpen(true);
    } catch (error) {
      toast.error(getAgreementTemplateErrorMessage(error), {
        autoClose: 5000,
        containerId: "Main",
      });
    } finally {
      setSearchFetchLoading(false);
    }
  };

  // const onDelete = async (agtId, agtTitle) => {
  //   const confirmed = window.confirm(
  //     `Delete template "${agtTitle}"? This is a soft delete and can be restored from backend only.`
  //   );
  //   if (!confirmed) return;
  //   try {
  //     await deleteAgreementTemplate(agtId);
  //     toast.success("Agreement template deleted successfully.", {
  //       autoClose: 5000,
  //       containerId: "Main",
  //     });
  //     // Re-run search to refresh results
  //     const data = await fetchTemplates();
  //     if (searchClicked) {
  //       const code = codeRef.current?.value ?? "";
  //       const title = titleRef.current?.value ?? "";
  //       const type = typeRef.current?.value ?? "";
  //       setSearchRes(runLocalFilter(data, code, title, language, active, type));
  //     }
  //   } catch (error) {
  //     toast.error(getAgreementTemplateErrorMessage(error), {
  //       autoClose: 5000,
  //       containerId: "Main",
  //     });
  //   }
  // };

  return (
    <>
      {/* Tab bar */}
      <div
        className="ui attached tabular"
        style={{ display: "flex", justifyContent: "space-around" }}
      >
        <div className="ui top attached tabular menu">
          <Tab
            Icon={Info}
            active={tab === 1}
            onClick={() => setTab(1)}
            label="Agreement Templates"
            form={FORMS.AGREEMENT.AGR.form}
            module={MODULES.Master}
            authenticated={true}
          />
          <Tab
            Icon={Eye}
            active={tab === 2}
            onClick={() => setTab(2)}
            label="Render Preview"
            form={FORMS.AGREEMENT.AGR.form}
            module={MODULES.Master}
            authenticated={true}
          />
        </div>

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: "0px" }}
          className="ui tabular menu top attached"
        >
          {tab === 1 && (
            <Button
              template="drawerOpen"
              onClick={openCreateDrawer}
              authenticated={true}
              form={FORMS.AGREEMENT.AGR.form}
              module={MODULES.Master}
              editMode={false}
            />
          )}
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        {/* Accordion */}
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              {/* Refresh icon — visible after a search is executed */}
              <i
                className="refresh icon primary button"
                style={{
                  display: resetVisible ? "flex" : "none",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveAccordion1(true);
                }}
              ></i>
            </div>
          </div>

          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isSearchLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <h4 className="ui dividing header">
                  Agreement Template Information
                </h4>

                <div className="three fields">
                  <Input
                    label="Template Code"
                    placeholder="Template Code"
                    name="agtCode"
                    ref={codeRef}
                  />
                  <Input
                    label="Template Title"
                    placeholder="Template Title"
                    name="agtTitle"
                    ref={titleRef}
                  />
                  <Input
                    label="Type"
                    placeholder="Type"
                    type="number"
                    name="agtType"
                    ref={typeRef}
                  />
                </div>

                <div className="two fields">
                  <Dropdown
                    clearable
                    search={true}
                    label="Language"
                    placeholder="Select Language"
                    options={languageOptions}
                    value={language}
                    onChange={(e, data) => setLanguage(data.value || null)}
                  />
                  <Dropdown
                    clearable
                    label="Active"
                    placeholder="Select Active"
                    options={activeOptions}
                    value={active}
                    onChange={(e, data) => setActive(data.value ?? null)}
                  />
                  <Dropdown
                    clearable
                    label="Status"
                    placeholder="Select Status"
                    options={statusOptions}
                    value={status}
                    onChange={(e, data) => setStatus(data.value ?? null)}
                  />
                </div>

                <div className="three fields">
                  <Input
                    label="From Date"
                    type="date"
                    placeholder="From Date"
                    value={fromDate}
                    max={toDate ?? new Date().toISOString().split("T")[0]}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                  <Input
                    label="To Date"
                    type="date"
                    placeholder="To Date"
                    value={toDate}
                    min={fromDate}
                    max={new Date().toISOString().split("T")[0]}
                    disabled={!fromDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => handleSearchSubmit(e)}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => handleSearchReset(e)}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Search results */}
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                searchable={false}
                content={searchRes}
                isLoading={searchFetchLoading}
                heading={
                  searchRes?.length > 0 ? "Agreement Template List" : ""
                }
                config={[
                  {
                    headerName: "Code",
                    field: "agtCode",
                    minWidth: 160,
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Title",
                    field: "agtTitle",
                    minWidth: 220,
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  // {
                  //   headerName: "Subject",
                  //   field: "agtSubject",
                  //   minWidth: 220,
                  //   cellRenderer: ResizableScrollingTextRenderer,
                  // },
                  {
                    headerName: "Language",
                    field: "agtLanguage",
                    minWidth: 120,
                  },
                  {
                    headerName: "Type",
                    field: "agtType",
                    minWidth: 100,
                  },
                  {
                    headerName: "Status",
                    field: "agtStatus",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "agtActive",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={(props) =>
                  openEditDrawer(props?.data?.agtId)
                }
              />
            )}
          </>
        )}
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 2 ? "block" : "none" }}
      >
        <AgreementTemplateRenderPreview key={tab} templates={templates} />
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={() => setIsDrawerOpen(false)}
        head={
          <div style={{ padding: "10px 16px" }}>
            <h3>
              <FileText size={18} style={{ marginRight: "8px" }} />
              {editMode
                ? `Edit Template${
                    selectedTemplate?.agtCode
                      ? `: ${selectedTemplate.agtCode}`
                      : ""
                  }`
                : "Add Agreement Template"}
            </h3>
          </div>
        }
        body={
          <AgreementTemplateForm
            editMode={editMode}
            currentValues={selectedTemplate || {}}
            onCancel={() => setIsDrawerOpen(false)}
            onSuccess={async () => {
              setIsDrawerOpen(false);
              if (searchClicked) {
                setSearchSubmitLoading(true);
                const response = await searchAgreementTemplate({
                  agtCode: codeRef.current?.value ?? "",
                  agtTitle: titleRef.current?.value ?? "",
                  agtType: typeRef.current?.value ?? "",
                  agtLanguage: language,
                  agtActive: active,
                  agtStatus: status,
                  fromDate: fromDate || null,
                  toDate: toDate || null,
                });
                setSearchRes(response);
                setSearchSubmitLoading(false);
              } else {
                setIsSearchLoading(true);
                await fetchTemplates();
                setIsSearchLoading(false);
              }
            }}
          />
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default AgreementTemplate;
